<template>
    <v-container fluid>
        <div v-if="isLoaded">
            <v-row justify="end">
                <information :text="`${text[locale.split('-')[0]]}`"/>
            </v-row>
            
            <div v-if="finances?.length" class="mb-12 body-2">
                <!--table head-->
                <v-divider color="black" />
                <v-row class="subtitle-2 text-center" no-gutters>
                    <v-col class="text-left">{{'Last year' | localize }}, $</v-col>
                    <v-divider vertical color="black"></v-divider>
                    <v-col v-for="item in finances" :key="item.id" class="text-right mr-2">
                        {{ item.team?.name.split('_')[0] }}
                    </v-col>
                    <v-divider vertical color="black"></v-divider>
                </v-row>
                <v-divider color="black"/>

                <!--finances tab-->
                <v-row class="subtitle-2 text-left font-weight-bold my-2" no-gutters>{{'Profit and Loss Statement' | localize }}</v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Sales' | localize }}, {{'total' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`sales_${item.id}`" class="text-right mr-2">
                        {{ formatNumber(numberRound(item.sales)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Other' | localize }} {{'operational incomes' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`incomes_${item.id}`" class="text-right mr-2">
                        {{ formatNumber(numberRound(item.other_incomes)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Cost of Goods Sold' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`coGs_${item.id}`" class="text-right mr-2">
                           {{ formatNumber(numberRound(item.CoGs)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row class="font-weight-bold" no-gutters>
                    <v-col class="text-left">{{'Gross margin' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`grossM_${item.id}`" class="text-right mr-2">
                        {{ formatNumber(numberRound(grossMargin(item))) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'R&D' | localize }} {{'costs' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`rd_${item.id}`" class="text-right mr-2">
                           {{ formatNumber(numberRound(item.rd_expenses)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'M&S' | localize }} {{'costs' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`ms_${item.id}`" class="text-right mr-2">
                        {{ formatNumber(numberRound(item.ms_expenses)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'G&A' | localize }} {{'costs' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`ga_${item.id}`" class="text-right mr-2">
                        {{ formatNumber(numberRound(item.ga_expenses)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row class="font-weight-bold" no-gutters>
                    <v-col class="text-left">{{'EBITDA' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`ebitda_${item.id}`" class="text-right mr-2">
                        <span v-if="ebitda(item) < 0" class="error--text">{{ formatNumber(numberRound(ebitda(item))) }}</span>
                        <span v-else>{{ formatNumber(numberRound(ebitda(item))) }}</span>    
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Depreciation' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`dep_${item.id}`" class="text-right mr-2">
                           {{ formatNumber(numberRound(item.depreciation))}}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row class="font-weight-bold" no-gutters>
                    <v-col class="text-left">{{'EBIT' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`ebit_${item.id}`" class="text-right mr-2">
                        <span v-if="ebit(item) < 0" class="error--text">{{ formatNumber(numberRound(ebit(item))) }}</span>
                        <span v-else>{{ formatNumber(numberRound(ebit(item))) }} </span>
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Interest rate' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`interest_${item.id}`" class="text-right mr-2">
                         {{ formatNumber(numberRound(item.interests)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Inventories and assets write-off' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`accum_off_${item.id}`" class="text-right mr-2">
                        {{ formatNumber(numberRound(item.accum_write_off)) }}  
                    </v-col>
                </v-row>
                <v-divider />
                <v-row class="font-weight-bold" no-gutters>
                    <v-col class="text-left">{{'Gross profit' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`gross_prof_${item.id}`" class="text-right mr-2">
                        <span v-if="grossProfit(item) < 0" class="error--text">{{ formatNumber(numberRound(grossProfit(item))) }}</span>
                        <span v-else>{{ formatNumber(numberRound(grossProfit(item))) }}</span> 
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Profit tax' | localize }}(12%)</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`12_${item.id}`" class="text-right mr-2">
                        <span v-if="grossProfit(item) < 0"> - </span>
                        <span v-else>{{ formatNumber(numberRound(grossProfit(item) * 0.12)) }}</span>   
                    </v-col>
                </v-row>
                <v-divider />
                <v-row class="font-weight-bold" no-gutters>
                    <v-col class="text-left">{{'Net profit' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`net_profit_${item.id}`" class="text-right mr-2">
                        <span v-if="grossProfit(item) < 0" class="error--text"> {{ formatNumber(numberRound(grossProfit(item))) }} </span>
                        <span v-else>{{ formatNumber(numberRound(grossProfit(item) - grossProfit(item) * 0.12)) }}</span>   
                    </v-col>
                </v-row>
                <v-divider color="black" /> 
                <v-row class="subtitle-2 text-left font-weight-bold my-2" no-gutters>{{'Cash Flow Statement' | localize }}</v-row>
                <v-divider color="black" /> 
                <v-row no-gutters>
                    <v-col class="text-left">{{'Cash received from operations' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`cash_rec_${item.id}`" class="text-right mr-2">
                        {{ formatNumber(numberRound(item.sales + item.other_incomes)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Cash spent on operations' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`cash_spend_${item.id}`" class="text-right mr-2">
                           {{ formatNumber(numberRound(cashSpent(item))) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row class="font-weight-bold" no-gutters>
                    <v-col class="text-left">{{'Net cash flow from operations' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`net_cash_operations_${item.id}`" class="text-right mr-2">
                        <span v-if="item.sales + item.other_incomes - cashSpent(item) < 0" class="error--text">
                            {{ formatNumber(numberRound(item.sales + item.other_incomes - cashSpent(item))) }}
                        </span>
                        <span v-else>
                            {{ formatNumber(numberRound(item.sales + item.other_incomes - cashSpent(item))) }}
                        </span> 
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Cash spent on investments' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`cash_invest_${item.id}`" class="text-right mr-2">
                           {{ formatNumber(numberRound(item.investments)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Cash received from financial activities' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`cash_act_${item.id}`" class="text-right mr-2">
                        <span v-if="item.borrowings >= 0">{{ formatNumber(numberRound(item.borrowings + item.emergency_loan)) }}</span>
                        <span v-else>{{ formatNumber(numberRound(item.emergency_loan)) }}</span>   
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Cash spent on financial activities' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="item.id" class="text-right mr-2">
                        <span v-if="item.borrowings >= 0"> 0 </span>
                        <span v-else>{{ formatNumber(numberRound(Math.abs(item.borrowings))) }}</span>  
                    </v-col>
                    </v-row>
                
                <v-divider />
                <v-row class="font-weight-bold" no-gutters>
                    <v-col class="text-left">{{'Net cash from financial activities' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`net_cash_activ_${item.id}`" class="text-right mr-2">
                            {{ formatNumber(numberRound(netCashActivity(item))) }}  
                    </v-col>
                </v-row>
                <v-divider />
                <v-row class="font-weight-bold" no-gutters>
                    <v-col class="text-left">{{'Net cash flow' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`cash_flow_${item.id}`" class="text-right mr-2">
                        <span v-if="netCashFlow(item) < 0" class="error--text">
                            {{ formatNumber(numberRound(netCashFlow(item))) }}
                        </span>
                        <span v-else>
                           {{ formatNumber(numberRound(netCashFlow(item))) }}
                        </span>
                    </v-col>
                </v-row>
                <v-divider />
                <v-row class="font-weight-bold" no-gutters>
                    <v-col class="text-left">{{'Net cash position' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`cash_${item.id}`" class="text-right mr-2">
                           {{ formatNumber(numberRound(item.cash)) }}
                    </v-col>
                </v-row>
                <v-divider color="black" />
                <v-row class="subtitle-2 text-left font-weight-bold my-2" no-gutters>{{'Balance sheet' | localize }}</v-row>
                <v-divider color="black" />

                <v-row class="text-left font-weight-bold my-2" no-gutters>{{'Assets' | localize }}</v-row>
                <v-divider /> 
                <v-row no-gutters>
                    <v-col class="text-left">{{'Assets' | localize }} ({{'production and retail' | localize }})</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`assets_${item.id}`" class="text-right mr-2">
                        {{ formatNumber(numberRound(item.fixed_assets)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Accumulated depreciation' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`accum_${item.id}`" class="text-right mr-2">
                        {{ formatNumber(numberRound(item.accumulated_depreciation)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Cash' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`ch_${item.id}`" class="text-right mr-2">
                           {{ formatNumber(numberRound(item.cash)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Inventories' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`inv_${item.id}`" class="text-right mr-2">
                           {{ formatNumber(numberRound(item.inventories)) }}
                    </v-col>
                </v-row>
                <v-divider /> 

                <!-- total assets -->
                <v-divider color="black" />
                <v-row class="font-weight-bold my-2" no-gutters>
                    <v-col class="text-left">{{'Total assets' | localize }}</v-col>
                    <v-col v-for="item in finances" :key="`inv_${item.id}`" class="text-right mr-2">
                           {{ formatNumber(numberRound(totalAssets(item))) }}
                    </v-col>
                </v-row>
                <v-divider color="black" />

                <v-row class="text-left font-weight-bold my-2" no-gutters>{{'Liabilities' | localize}}</v-row>
                <v-divider /> 
                <v-row no-gutters>
                    <v-col class="text-left">{{'Long term debt' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`debt_${item.id}`" class="text-right mr-2">
                           {{ formatNumber(numberRound(item.long_term_debt)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Emergency loan' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`emergency_${item.id}`" class="text-right mr-2">
                        {{ formatNumber(numberRound(item.emergency_loan)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Contributed capital' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="`capital_${item.id}`" class="text-right mr-2">
                        {{ formatNumber(numberRound(item.contributed_capital)) }}
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="text-left">{{'Accumulated profit' | localize }}</v-col>
                    <v-divider vertical></v-divider>
                    <v-col v-for="item in finances" :key="item.id" class="text-right mr-2">
                        <span v-if="item.retained_profit < 0" class="error--text">
                            {{ formatNumber(numberRound(item.retained_profit)) }}
                        </span>
                        <span v-else>
                            {{ formatNumber(numberRound(item.retained_profit)) }}
                        </span>
                    </v-col>
                </v-row>
                <v-divider />
                <!-- total liabilities -->
                <v-divider color="black" />
                <v-row class="font-weight-bold my-2" no-gutters>
                    <v-col class="text-left">{{'Total liabilities' | localize }}</v-col>
                    <v-col v-for="item in finances" :key="`inv_${item.id}`" class="text-right mr-2">
                           {{ formatNumber(numberRound(totalLiabilities(item))) }}
                    </v-col>
                </v-row>
                <v-divider color="black" />
            </div>
        </div>
        <!-- Loader -->
		<v-row v-else justify="center" class="mt-12">
            <loader />
        </v-row>
    </v-container>
</template>
<script>
import { localeNumber, roundNumber } from '../../../functions/common';
import Information from '../../../layouts/Information.vue';
import Loader from '../../../layouts/Loader.vue';
export default {
    name: 'MarketFinances',
    data() {
        return {
            isLoaded: false,
            types: ['Classic', 'Business', 'Casual', 'Innovative', 'Art'],
            text: {
                en: 'Standard financial reports are published by each company annually, based on its year-end results.',
                ro: 'Fiecare companie publică anual rapoarte financiare standard, pe baza rezultatelor înregistrate la sfârșitul exercițiului financiar.',
                ru: 'Каждая компания ежегодно публикует стандартную финансовую отчетность на основе результатов за год.'
            },
        }
    },
    computed: {
        locale() {
           return this.$store.getters.locale; 
        },
        game() {
            return this.$store.getters.game
        },
        finances() {
            return this.$store.getters.finances_all;
        },
        intermediary() {
            return this.$store.getters.intermediary_all;
        }
    },
    methods: {
        formatNumber(num) {
            return localeNumber(num, this.locale);
        },
        numberRound(num) {
            return roundNumber(num);
        },
        grossMargin(item) {
            return item.sales + item.other_incomes - item.CoGs;
        },
        ebitda(item) {
            //Gross margin - F14 - F13 - F12
            const res = roundNumber(this.grossMargin(item) - (item.rd_expenses + item.ga_expenses + item.ms_expenses), 0);
            return res;
        },
        ebit(item) {
            //EBITDA - F15 - F16
            const res = roundNumber(this.ebitda(item) - item.depreciation, 0);
            return res;
        },
        grossProfit(item) {
            const res = this.numberRound(this.ebit(item) - item.interests - item.accum_write_off, 0);
            return res
        },
        cashSpent(item) {
            let inventories_increace = item.inventories_increace > 0 ? item.inventories_increace : 0;
            let profit_tax = this.grossProfit(item) > 0 ?  this.grossProfit(item) * 0.12 : 0;
            const res = item.CoGs + item.rd_expenses + item.ms_expenses + item.ga_expenses + item.interests + profit_tax + inventories_increace;
            return res;
        },
        netCashActivity(item) {
            if (item.borrowing < 0) {
                return (item.emergency_loan - item.borrowings);
            }
            return (item.borrowings + item.emergency_loan);
        },
        netCashFlow(item) {
            /* Net cash from operations - Cash spent on investments + Net cash from financial activities */
            const res = item.sales + item.other_incomes - this.cashSpent(item) - item.investments + this.netCashActivity(item);
            return res;
        },
        retailAssets(item) {
            let res = 0
            if (this.intermediary?.length > 0) {
                const matched = this.intermediary.find((inter) => inter.teamId == item.teamId);
                if (matched) {
                    res = matched.retail_assets;
                }
            }
            return res;
        },
        totalAssets(item) {
            let res = 0;
            res = item.fixed_assets - item.accumulated_depreciation + item.cash + item.inventories;
            return res;
        },
        totalLiabilities(item) {
            return item.long_term_debt + item.emergency_loan + item.contributed_capital + item.retained_profit;
        }
        
    },
    async mounted() {
        if (this.game && this.game.round > 0) {

            const obj = {
                gameId: this.game.id,
                round: this.game.round - 1,
            }

            if (this.finances?.length < 1 ) {
                try {
                    await this.$store.dispatch('fetchFinances', obj);
                } catch(error) {
                    await this.$store.dispatch('setError', error.message);
                }
            }

            if (this.intermediary?.length < 1) {
				try {
					await this.$store.dispatch('fetchIntermediary', obj);
				} catch (error) {
					await this.$store.dispatch('setError', error.message);
				}
            }
        }

        this.isLoaded = true;
    },
    components: { Information, Loader }
}
</script>